import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from '../../../shared/common.service';
import { ToastService } from '../../../shared/toast.service';
import { LoaderService } from '../../../shared/loader.service';
import { finalize } from 'rxjs/operators';

type Tier = 'silver' | 'gold' | 'platinum' | 'other';

interface UiPackage {
  _id: string;
  name: string;
  tier: Tier;
  price: number;
  subscriptionImage?: string;
  installmentExist?: boolean;
}

@Component({
  selector: 'app-flexible-learning',
  templateUrl: './flexible-learning.component.html',
  styleUrls: ['./flexible-learning.component.scss']
})
export class FlexibleLearningComponent implements OnInit {
  isLoading = false;

  // ✅ list used by *ngFor
  packagesForUi: UiPackage[] = [];

  constructor(
    private router: Router,
    private commonService: CommonService,
    private toastService: ToastService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.loadPackagesForCards();
  }

  private loadPackagesForCards(): void {
    this.isLoading = true;

    // IMPORTANT:
    // If your LoaderInterceptor already shows global loader, remove show/hide calls to avoid pending mismatch.
    this.loaderService.show();

    this.commonService.getPackages()
      .pipe(finalize(() => {
        this.isLoading = false;
        this.loaderService.hide();
      }))
      .subscribe({
        next: (res: any) => {
          if (Number(res?.status) !== 1 || !Array.isArray(res?.data)) {
            this.toastService.showError('Failed to load packages.');
            return;
          }

          const mapped: UiPackage[] = res.data.map((p: any) => ({
            _id: p._id,
            name: p.name || 'Unnamed Package',
            tier: this.detectTier(p?.name),
            price: typeof p?.price === 'number' ? p.price : 0,
            subscriptionImage: p?.subscriptionImage,
            installmentExist: p?.installmentExist === true
          }));

          // ✅ Force order: Silver -> Gold -> Platinum -> Others
          const order: Record<Tier, number> = { silver: 1, gold: 2, platinum: 3, other: 9 };

          this.packagesForUi = mapped
            .sort((a, b) => order[a.tier] - order[b.tier]);
        },
        error: (err) => {
          console.error('getPackages error', err);
          this.toastService.showError('Failed to load packages. Please try again.');
        }
      });
  }

  private detectTier(name: string): Tier {
    const n = (name || '').toLowerCase();
    if (n.includes('silver')) return 'silver';
    if (n.includes('gold')) return 'gold';
    if (n.includes('platinum')) return 'platinum';
    return 'other';
  }

  // ✅ Dynamic price display
  getPriceText(p: UiPackage): string {
    if (p.price === 0) return 'Custom Pricing';
    return `$${p.price.toLocaleString()}`;
  }

  // ✅ Image fallback per tier
  getLogo(p: UiPackage): string {
    if (p.subscriptionImage) return p.subscriptionImage;

    // fallback local assets (only used if API has no image)
    if (p.tier === 'silver') return '../../../../assets/silver badge.jpeg';
    if (p.tier === 'gold') return '../../../../assets/gold badge.jpeg';
    if (p.tier === 'platinum') return '../../../../assets/platinum badge.jpeg';
    return '../../../../assets/platinum badge.jpeg';
  }

  // ✅ Buy now uses packageId (works for any tier)
  buyNow(pkg: UiPackage): void {
    if (this.isLoading) return;

    const priceDisplay = pkg.price === 0 ? 'Custom Pricing' : `$${pkg.price}`;

    this.router.navigate(['/billing-form'], {
      queryParams: {
        packageId: pkg._id,
        package: pkg.name,
        price: priceDisplay,
        installment: pkg.installmentExist === true
      }
    });
  }
}
